import { useMemo, type CSSProperties } from 'react';
import { useTheme } from '@course-authoring/shared-types';

export function createLibraryPanelStyles(
  tokens: ReturnType<typeof useTheme>['tokens']
): Record<string, CSSProperties> {
  return {
    content: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      padding: tokens.spacing.lg,
    },
    toolbar: {
      display: 'flex',
      gap: '12px',
      marginBottom: tokens.spacing.lg,
      flexShrink: 0,
    },
    searchContainer: {
      flex: 1,
      minWidth: 0,
    },
    searchInput: {
      width: '100%',
      boxSizing: 'border-box' as const,
      padding: '8px 12px',
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: tokens.radius.sm,
      fontSize: '14px',
      outline: 'none',
      backgroundColor: tokens.backgrounds.surface,
      color: tokens.text.primary,
    },
    aiButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '8px 12px',
      backgroundColor: tokens.ui.selectionBorder,
      color: tokens.text.onBrand,
      border: 'none',
      borderRadius: tokens.radius.sm,
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    errorMessage: {
      padding: tokens.spacing.lg,
      backgroundColor: tokens.ui.errorBackground,
      color: tokens.ui.errorText,
      borderRadius: tokens.radius.sm,
      marginBottom: tokens.spacing.lg,
      fontSize: '14px',
    },
    uploadErrorContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: tokens.spacing.lg,
      backgroundColor: tokens.ui.overlayMarkerBackground,
      borderRadius: tokens.radius.sm,
      marginBottom: tokens.spacing.lg,
    },
    uploadErrorMessage: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: tokens.ui.overlayMarker,
      fontSize: '14px',
    },
    uploadErrorIcon: {
      fontSize: '18px',
    },
    retryButton: {
      padding: '4px 12px',
      backgroundColor: tokens.ui.overlayMarker,
      color: tokens.text.onBrand,
      border: 'none',
      borderRadius: '4px',
      fontSize: '14px',
      cursor: 'pointer',
    },
    generatedPreview: {
      marginBottom: tokens.spacing.lg,
      padding: tokens.spacing.lg,
      backgroundColor: tokens.backgrounds.lessonSectionTinted,
      borderRadius: tokens.radius.sm,
      border: `2px solid ${tokens.ui.selectionBorder}`,
    },
    generatedPreviewHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px',
    },
    generatedPreviewLabel: {
      fontSize: '14px',
      fontWeight: 500,
      color: tokens.ui.selectionBorder,
    },
    clearGeneratedButton: {
      width: '24px',
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      color: tokens.text.muted,
      border: 'none',
      borderRadius: '50%',
      fontSize: '18px',
      cursor: 'pointer',
    },
    generatedPreviewImage: {
      width: '100%',
      maxHeight: '150px',
      objectFit: 'contain',
      borderRadius: '4px',
    },
    gridContainer: {
      flex: 1,
      overflow: 'auto',
    },
    noResults: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px',
      color: tokens.text.muted,
    },
    noResultsIcon: {
      fontSize: '48px',
      marginBottom: tokens.spacing.lg,
    },
    noResultsText: {
      fontSize: '18px',
      fontWeight: 500,
      marginBottom: '4px',
    },
    noResultsHint: {
      fontSize: '14px',
      textAlign: 'center',
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '8px',
      padding: tokens.spacing.lg,
      borderTop: `1px solid ${tokens.text.subtle}`,
      backgroundColor: tokens.backgrounds.surface,
    },
    backButton: {
      padding: '8px 12px',
      fontSize: '14px',
      fontWeight: 500,
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: '6px',
      background: 'none',
      cursor: 'pointer',
      color: tokens.text.muted,
      minWidth: '44px',
      minHeight: '44px',
    },
    cancelButton: {
      padding: '8px 16px',
      backgroundColor: 'transparent',
      color: tokens.text.muted,
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: tokens.radius.sm,
      fontSize: '14px',
      cursor: 'pointer',
    },
    editAIButton: {
      padding: '8px 16px',
      backgroundColor: tokens.backgrounds.lessonSectionTinted,
      color: tokens.text.primary,
      border: `1px solid ${tokens.text.subtle}`,
      borderRadius: tokens.radius.sm,
      fontSize: '14px',
      cursor: 'pointer',
    },
    insertButton: {
      padding: '8px 16px',
      backgroundColor: tokens.ui.selectionBorder,
      color: tokens.text.onBrand,
      border: 'none',
      borderRadius: tokens.radius.sm,
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
    },
    insertButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    buttonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    placeholder: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px',
      color: tokens.text.muted,
    },
    placeholderIcon: {
      fontSize: '48px',
      marginBottom: tokens.spacing.lg,
    },
    placeholderText: {
      fontSize: '18px',
      fontWeight: 500,
      marginBottom: tokens.spacing.lg,
    },
  };
}

export function useLibraryPanelStyles() {
  const { tokens } = useTheme();
  return useMemo(() => createLibraryPanelStyles(tokens), [tokens]);
}
